import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Grid,
  Alert,
  IconButton,
  Typography,
  CircularProgress
} from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { UpdateUserRequest, UserRole, User } from '../../models/User';
import { useAuth } from '../../hooks/useAuth';
import { Hotel } from '../../models/Hotel';
import { Zone } from '../../models/Zone';
import * as hotelService from '../../services/hotelService';
import * as zoneService from '../../services/newZoneService';

interface EditUserModalProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  onSubmit: (userId: number, userData: UpdateUserRequest) => Promise<void>;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  onClose,
  user,
  onSubmit
}) => {
  const { userRole, user: currentUser } = useAuth();
  const currentUserRole = userRole || UserRole.HotelUser;

  const [formData, setFormData] = useState<UpdateUserRequest>({
    username: '',
    role: UserRole.HotelUser,
    hotelId: undefined,
    assignedZoneIds: [],
  });

  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && open) {
      setFormData({
        username: user.username,
        role: user.role,
        hotelId: user.hotelId,
        assignedZoneIds: user.assignedZoneIds || [],
      });
      setSubmitError('');
    }
  }, [user, open]);

  useEffect(() => {
    const loadHotels = async () => {
      if (currentUserRole === UserRole.HotelManager) {
        try {
          const data = await hotelService.getHotels();
          setHotels(data);
        } catch (err) {
          console.error('Failed to load hotels', err);
        }
      }
    };
    if (open) loadHotels();
  }, [currentUserRole, open]);

  useEffect(() => {
    const loadZones = async () => {
      try {
        setLoading(true);
        if (formData.hotelId) {
          const data = await zoneService.getZonesByHotel(formData.hotelId);
          setZones(data);
        }
      } catch (err) {
        console.error('Failed to load zones', err);
      } finally {
        setLoading(false);
      }
    };
    if (formData.role === UserRole.HotelUser && formData.hotelId && open) {
      loadZones();
    }
  }, [formData.hotelId, formData.role, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name as string]: value });
    if (name === 'role') {
      const role = value as UserRole;
      if (role === UserRole.HotelManager) {
        setFormData(prev => ({ ...prev, role, assignedZoneIds: [] }));
      }
    }
    if (name === 'hotelId') {
      setFormData(prev => ({ ...prev, [name as string]: value, assignedZoneIds: [] }));
    }
  };

  const handleZoneChange = (event: SelectChangeEvent<number[]>) => {
    const { value } = event.target;
    setFormData({
      ...formData,
      assignedZoneIds: typeof value === 'string' ? [parseInt(value, 10)] : value as number[],
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSubmitError('');
    try {
      setSubmitting(true);
      await onSubmit(user.id, formData);
    } catch (err) {
      console.error('Error updating user:', err);
      const error = err as any;
      if (typeof error.response?.data === 'string') {
        setSubmitError(error.response.data);
      } else if (error.response?.data?.title) {
        setSubmitError(error.response.data.title);
      } else if (error.message) {
        setSubmitError(error.message);
      } else {
        setSubmitError('Kullanıcı güncellenemedi. Lütfen tekrar deneyin.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitError('');
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden',
          maxHeight: '85vh'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0',
        py: 2, px: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px',
            backgroundColor: 'rgba(66, 148, 227, 0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <EditRoundedIcon sx={{ fontSize: 18, color: '#4294e3' }} />
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e' }}>
            Kullanıcı Düzenle
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small"
          sx={{ width: 32, height: 32, color: '#999', '&:hover': { color: '#666', backgroundColor: 'rgba(0,0,0,0.04)' } }}>
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {submitError && (
          <Alert severity="error" sx={{
            mb: 2, borderRadius: '10px',
            backgroundColor: 'rgba(229, 57, 53, 0.05)',
            border: '1px solid rgba(229, 57, 53, 0.15)',
          }}>
            {submitError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                required fullWidth size="small" name="username" label="Kullanıcı Adı"
                value={formData.username} onChange={handleChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            {currentUserRole === UserRole.HotelManager && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Rol</InputLabel>
                  <Select name="role" value={formData.role} label="Rol"
                    onChange={handleChange as any} sx={{ borderRadius: '10px' }}>
                    <MenuItem value={UserRole.HotelManager}>Hotel Manager</MenuItem>
                    <MenuItem value={UserRole.HotelUser}>Hotel Staff</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {currentUserRole !== UserRole.HotelManager && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Rol"
                  value={formData.role === UserRole.HotelManager ? 'Hotel Manager' : 'Hotel Staff'}
                  disabled sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
              </Grid>
            )}

            {currentUserRole === UserRole.HotelManager && formData.role !== UserRole.HotelManager && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Hotel</InputLabel>
                  <Select name="hotelId" value={formData.hotelId || ''} label="Hotel"
                    onChange={handleChange as any} sx={{ borderRadius: '10px' }}>
                    {hotels.map((hotel) => (
                      <MenuItem key={hotel.id} value={hotel.id}>{hotel.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {formData.role === UserRole.HotelUser && formData.hotelId && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Atanmış Zone'lar</InputLabel>
                  <Select
                    multiple name="assignedZoneIds" value={formData.assignedZoneIds || []}
                    onChange={handleZoneChange}
                    input={<OutlinedInput label="Atanmış Zone'lar" />}
                    sx={{ borderRadius: '10px' }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as number[]).map((zoneId) => {
                          const zone = zones.find(z => z.id === zoneId);
                          return <Chip key={zoneId} label={zone?.name || zoneId} size="small"
                            sx={{ height: 22, fontSize: '0.75rem', borderRadius: '6px' }} />;
                        })}
                      </Box>
                    )}
                  >
                    {zones.map((zone) => (
                      <MenuItem key={zone.id} value={zone.id}>{zone.name}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Bu kullanıcının erişebileceği zone'ları seçin</FormHelperText>
                </FormControl>
              </Grid>
            )}
          </Grid>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
            <Button onClick={handleClose} disabled={submitting}
              sx={{
                textTransform: 'none', fontWeight: 500, fontSize: '0.85rem',
                borderRadius: '10px', px: 2.5, color: '#666',
                backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#eee' }
              }}>
              Vazgeç
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || loading}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: '0.85rem',
                borderRadius: '10px', px: 2.5,
                backgroundColor: '#4294e3', color: '#fff',
                '&:hover': { backgroundColor: '#3680c9' },
                '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#999' }
              }}>
              {submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Güncelle'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserModal;
